import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  IAddUserRequestBody,
  IEditUserRequestBody,
  ILoginRequestBody,
  IResetCustomerPassword,
  IUserState,
  initialUserInfo,
} from "./user.model";
import { Login } from "../services/user.api";
import { AxiosError } from "axios";
import { RootState } from "../store";
import { isValidString } from "../../Utils/validators";
import storage from "redux-persist/lib/storage";
import { persistReducer } from "redux-persist";

const initialState: IUserState = {
  userInfo: initialUserInfo,
  isAuthenticating: false,
  hasError: false,
  errorMessage: "",
  listUser: [],
  userListFetching: false,
  userListFetchError: "",
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    logoutUser: (state, action) => {
      state.userInfo = initialUserInfo;
      Login.instance.removeUserFromSession();
    },
    updateUser(
      state,
      action: PayloadAction<{
        firstname?: string;
        lastname?: string;
        email?: string;
        phone?: string;
        password?: string;
      }>
    ) {
      state.userInfo = { ...state.userInfo, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loginUser.pending, (state) => {
      state.isAuthenticating = true;
      state.errorMessage = "";
    });
    builder.addCase(
      loginUser.fulfilled,
      (state, action: PayloadAction<any>) => {
        if (action.payload.status === "success") {
          state.userInfo = action.payload?.data;
          state.isAuthenticating = false;
          state.hasError = false;
          state.errorMessage = "";
        } else {
          state.hasError = true;
          state.errorMessage = action.payload.error_message;
          state.isAuthenticating = false;
        }
      }
    );
    builder.addCase(loginUser.rejected, (state, action) => {
      state.userInfo = initialUserInfo;
      state.isAuthenticating = false;
      state.hasError = true;
      state.errorMessage = "";
    });
    builder.addCase(listUser.pending, (state) => {
      state.userListFetching = true;
      state.userListFetchError = "";
    });
    builder.addCase(listUser.fulfilled, (state, action: PayloadAction<any>) => {
      if (action.payload.status === "success") {
        state.listUser = action.payload?.data;
        state.userListFetching = false;
        state.userListFetchError = "";
      } else {
        state.userListFetchError = action.payload.error_message;
        state.userListFetching = false;
      }
    });
    builder.addCase(listUser.rejected, (state, action) => {
      state.listUser = [];
      state.userListFetching = false;
      state.userListFetchError = "";
    });
  },
});

export const { updateUser } = userSlice.actions;

export const loginUser = createAsyncThunk(
  "user/loginUser",
  async (payload: ILoginRequestBody) => {
    try {
      const { data } = await Login.instance.loginUser(payload);
      return data;
    } catch (error: AxiosError | any) {
      return {
        error_message: error?.response?.data?.message || "Something went wrong",
      };
    }
  }
);

export const addUser = createAsyncThunk(
  "user/addUser",
  async ({
    payload,
    successCB,
    failureCB,
  }: {
    payload: IAddUserRequestBody;
    successCB: (response: any) => void;
    failureCB: (error: any) => void;
  }) => {
    try {
      const { data } = await Login.instance.addUser(payload);
      if (data?.status === "success") {
        return successCB(data?.data);
      }
      return failureCB("Failed to add user");
    } catch (error: AxiosError | any) {
      failureCB(error || "Something went wrong");
    }
  }
);

export const editUser = createAsyncThunk(
  "user/editUser",
  async ({
    payload,
    successCB,
    failureCB,
  }: {
    payload: IEditUserRequestBody;
    successCB: (response: any) => void;
    failureCB: (error: any) => void;
  }) => {
    try {
      const { data } = await Login.instance.editUser(payload);
      if (data?.status === "success") {
        return successCB(data?.data);
      }
      return failureCB("Failed to add user");
    } catch (error: AxiosError | any) {
      failureCB(error || "Something went wrong");
    }
  }
);

export const listUser = createAsyncThunk("user/listUser", async () => {
  try {
    const { data } = await Login.instance.listUser();
    return data;
  } catch (error: AxiosError | any) {
    return {
      error_message: error?.response?.data?.message || "Something went wrong",
    };
  }
});

export const resetCustomerPassword = createAsyncThunk(
  "user/resetCustomerPassword",
  async ({
    payload,
    successCB,
    failureCB,
  }: {
    payload: IResetCustomerPassword;
    successCB: (response: any) => void;
    failureCB: (error: any) => void;
  }) => {
    try {
      const { data } = await Login.instance.resetCustomerPassword(payload);
      if (data?.status === "success") {
        return successCB(data?.data);
      }
      return failureCB("Failed to update customer password");
    } catch (error: AxiosError | any) {
      failureCB(error || "Something went wrong");
    }
  }
);

export const deleteUser = createAsyncThunk(
  "user/deleteUser",
  async ({
    userId,
    successCB,
    failureCB,
  }: {
    userId: string;
    successCB: (response: any) => void;
    failureCB: (error: any) => void;
  }) => {
    try {
      const { data } = await Login.instance.deleteUser(userId);
      return successCB(data?.data);
    } catch (error: AxiosError | any) {
      failureCB(error || "Something went wrong");
    }
  }
);

export const selectIsAuthenticated = (state: RootState) =>
  isValidString(state.user.userInfo.token);

export const selectIsAdmin = (state: RootState) =>
  isValidString(state.user.userInfo.role) &&
  state.user.userInfo.role.toLowerCase() === "admin";

export const selectCurrentUser = (state: RootState) => state.user.userInfo;

// Action creators are generated for each case reducer function
export const { logoutUser } = userSlice.actions;

const userPersistConfig = {
  key: "user",
  storage,
  whitelist: ["userInfo"],
};

export default persistReducer(userPersistConfig, userSlice.reducer);
